/**
 * NotesTab - Sub-tabbed view: Follow-Up | Notes | Bid Submissions
 */
import { useState } from "react";
import {
  User,
  Calendar,
  Check,
  Trash2,
  Edit2,
  Clock,
  AlertCircle,
  StickyNote,
  ListChecks,
  Send,
  MessageSquareText,
  Pencil,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent } from "../ui/tabs";
import { UnderlineTabsList as TabsList, UnderlineTabsTrigger as TabsTrigger } from "../ui/tabs-variants";
import { formatDate, formatDateTime } from "../../lib/formatters";
import BidSubmissionsCard from "./BidSubmissionsCard";

/* ── Helpers ─────────────────────────────────── */

const getDueDateStatus = (dueDate, isComplete) => {
  if (!dueDate || isComplete) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + "T00:00:00");
  const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: "Overdue", color: "destructive" };
  if (diffDays === 0) return { label: "Due Today", color: "destructive" };
  if (diffDays === 1) return { label: "Due Tomorrow", color: "warning" };
  return null;
};

/* ── Reusable note/follow-up card row ──────── */

const NoteRow = ({ note, isNoteType, onToggleComplete, onEdit, onDelete, canManageJobs, onEditCompletionNote, currentUserId }) => {
  const dueStatus = getDueDateStatus(note.due_date, note.is_complete);
  return (
    <Card
      className={`transition-all ${note.is_complete ? "opacity-60 bg-muted/30" : ""}`}
      data-testid={`note-${note.id}`}
    >
      <CardContent className="py-4">
        <div className="flex items-start gap-3">
          {isNoteType ? (
            <StickyNote className="w-5 h-5 mt-1 text-muted-foreground flex-shrink-0" />
          ) : (
            <Checkbox
              checked={note.is_complete}
              onCheckedChange={() => onToggleComplete(note)}
              className="mt-1"
              data-testid={`note-checkbox-${note.id}`}
            />
          )}

          <div className="flex-1 min-w-0">
            <p className={`whitespace-pre-wrap ${note.is_complete ? "line-through text-muted-foreground" : ""}`}>
              {note.content}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDateTime(note.created_at)}
              </span>
              <span>by {note.created_by_name}</span>

              {note.assigned_to_name && (
                <Badge variant="outline" className="gap-1">
                  <User className="w-3 h-3" />
                  {note.assigned_to_name}
                </Badge>
              )}

              {note.due_date && (
                <Badge variant={dueStatus?.color || "outline"} className="gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(note.due_date)}
                  {dueStatus && <span className="ml-1">({dueStatus.label})</span>}
                </Badge>
              )}

              {note.is_complete && note.completed_at && (
                <span className="flex items-center gap-1 text-green-600">
                  <Check className="w-3 h-3" />
                  Completed {formatDateTime(note.completed_at)} by {note.completed_by_name}
                </span>
              )}
            </div>

            {/* Completion Note - inline display */}
            {note.is_complete && note.completion_note && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md" data-testid={`completion-note-${note.id}`}>
                <div className="flex items-start gap-2">
                  <MessageSquareText className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-blue-900 whitespace-pre-wrap">{note.completion_note}</p>
                    <p className="text-xs text-blue-600 mt-1">
                      — {note.completion_note_by_name}, {formatDateTime(note.completion_note_at)}
                    </p>
                  </div>
                  {/* Allow the person who wrote the note to edit it */}
                  {note.completion_note_by_id === currentUserId && (
                    <Button
                      variant="ghost" size="icon"
                      className="h-6 w-6 text-blue-600 hover:text-blue-800"
                      onClick={() => onEditCompletionNote(note)}
                      data-testid={`edit-completion-note-${note.id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => onEdit(note)} data-testid={`edit-note-${note.id}`}>
              <Edit2 className="w-4 h-4" />
            </Button>
            {canManageJobs && (
              <Button
                variant="ghost" size="icon"
                onClick={() => onDelete(note.id)}
                className="text-destructive hover:text-destructive"
                data-testid={`delete-note-${note.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ── Main Component ────────────────────────── */

const NotesTab = ({
  job,
  users = [],
  currentUserId,
  canManageJobs,
  onAddNote,
  onEditNote,
  onDeleteNote,
  onUpdateJob,
}) => {
  const [activeSubTab, setActiveSubTab] = useState("follow_ups");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addType, setAddType] = useState("note");
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({ content: "", assigned_to_id: "", due_date: "" });
  const [completionDialog, setCompletionDialog] = useState(null); // note being completed
  const [completionNoteText, setCompletionNoteText] = useState("");

  const allNotes = job.notes || [];

  // Split into follow-ups and notes
  const followUps = allNotes
    .filter((n) => (n.note_type || "follow_up") === "follow_up")
    .sort((a, b) => {
      // Incomplete first, then by due date ascending (earliest first)
      if (a.is_complete !== b.is_complete) return a.is_complete ? 1 : -1;
      if (!a.is_complete && !b.is_complete) {
        if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
        if (a.due_date) return -1;
        if (b.due_date) return 1;
      }
      return (b.created_at || "").localeCompare(a.created_at || "");
    });

  const notesOnly = allNotes
    .filter((n) => (n.note_type || "follow_up") === "note")
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")); // newest first

  const hasBiddingGCs = (job.bidding_gcs || []).length > 0;
  // Bid Submissions: always visible when GCs are assigned, always editable for authorized users
  const showBidSubmissions = hasBiddingGCs;
  const canEditBidSubmissions = canManageJobs;
  const activeFollowUps = followUps.filter((n) => !n.is_complete).length;

  const resetForm = () => setFormData({ content: "", assigned_to_id: "", due_date: "" });

  const handleOpenAdd = (type) => {
    resetForm();
    setAddType(type);
    setShowAddDialog(true);
  };

  const handleOpenEdit = (note) => {
    setFormData({
      content: note.content,
      assigned_to_id: note.assigned_to_id || "",
      due_date: note.due_date || "",
    });
    setAddType(note.note_type || "follow_up");
    setEditingNote(note);
  };

  const handleSubmit = async () => {
    if (!formData.content.trim()) return;
    const isNoteType = addType === "note";
    const data = {
      content: formData.content.trim(),
      note_type: addType,
      assigned_to_id: isNoteType ? null : (formData.assigned_to_id || null),
      due_date: isNoteType ? null : (formData.due_date || null),
    };
    if (editingNote) {
      const success = await onEditNote(editingNote.id, data);
      if (success) { setEditingNote(null); resetForm(); }
    } else {
      const success = await onAddNote(data);
      if (success) { setShowAddDialog(false); resetForm(); }
    }
  };

  const handleToggleComplete = async (note) => {
    if (!note.is_complete) {
      // Opening completion dialog (marking complete)
      setCompletionNoteText("");
      setCompletionDialog(note);
    } else {
      // Un-completing — just toggle directly
      await onEditNote(note.id, { is_complete: false });
    }
  };

  const handleConfirmComplete = async () => {
    if (!completionDialog) return;
    await onEditNote(completionDialog.id, {
      is_complete: true,
      completion_note: completionNoteText.trim() || "",
    });
    setCompletionDialog(null);
    setCompletionNoteText("");
  };

  const handleEditCompletionNote = (note) => {
    setCompletionNoteText(note.completion_note || "");
    setCompletionDialog({ ...note, _editOnly: true });
  };

  const handleSaveCompletionNote = async () => {
    if (!completionDialog) return;
    await onEditNote(completionDialog.id, {
      completion_note: completionNoteText.trim() || "",
    });
    setCompletionDialog(null);
    setCompletionNoteText("");
  };

  const isNote = addType === "note";
  const dialogIsEditing = !!editingNote;
  const dialogType = isNote ? "Note" : "Follow-Up";

  return (
    <div className="space-y-4" data-testid="notes-tab-container">
      <Tabs defaultValue="follow_ups" onValueChange={setActiveSubTab} data-testid="notes-sub-tabs">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <TabsList data-testid="notes-sub-tabs-list">
            <TabsTrigger value="follow_ups" data-testid="sub-tab-follow-ups">
              <ListChecks className="w-4 h-4 mr-1.5" />
              Follow-Up
              {activeFollowUps > 0 && (
                <Badge variant="destructive" className="ml-1.5 h-5 min-w-[20px] px-1 text-[10px]">
                  {activeFollowUps}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="sub-tab-notes">
              <StickyNote className="w-4 h-4 mr-1.5" />
              Notes
              {notesOnly.length > 0 && (
                <span className="ml-1.5 text-xs text-muted-foreground">({notesOnly.length})</span>
              )}
            </TabsTrigger>
            {showBidSubmissions && (
              <TabsTrigger value="bid_submissions" data-testid="sub-tab-bid-submissions">
                <Send className="w-4 h-4 mr-1.5" />
                Bid Submissions
              </TabsTrigger>
            )}
          </TabsList>

          {activeSubTab === "follow_ups" && (
            <Button size="sm" onClick={() => handleOpenAdd("follow_up")} data-testid="add-followup-btn">
              <ListChecks className="w-4 h-4 mr-1.5" />
              Follow-Up
            </Button>
          )}
          {activeSubTab === "notes" && (
            <Button size="sm" onClick={() => handleOpenAdd("note")} data-testid="add-note-btn">
              <StickyNote className="w-4 h-4 mr-1.5" />
              Note
            </Button>
          )}
        </div>

        {/* ── Follow-Up Tab ──────────────────── */}
        <TabsContent value="follow_ups" data-testid="follow-ups-content">
          {followUps.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No follow-up items yet. Add one to track tasks and deadlines.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {followUps.map((note) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  isNoteType={false}
                  onToggleComplete={handleToggleComplete}
                  onEdit={handleOpenEdit}
                  onDelete={onDeleteNote}
                  canManageJobs={canManageJobs}
                  onEditCompletionNote={handleEditCompletionNote}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Notes Tab ──────────────────────── */}
        <TabsContent value="notes" data-testid="notes-only-content">
          {notesOnly.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No notes yet. Add a note to keep a record.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {notesOnly.map((note) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  isNoteType={true}
                  onToggleComplete={handleToggleComplete}
                  onEdit={handleOpenEdit}
                  onDelete={onDeleteNote}
                  canManageJobs={canManageJobs}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Bid Submissions Tab ────────────── */}
        {showBidSubmissions && (
          <TabsContent value="bid_submissions" data-testid="bid-submissions-content">
            <BidSubmissionsCard
              job={job}
              onUpdateBiddingGCs={(updatedGCs) => {
                if (onUpdateJob) onUpdateJob({ bidding_gcs: updatedGCs });
              }}
              canEdit={canEditBidSubmissions}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* ── Add / Edit Dialog ──────────────── */}
      <Dialog
        open={showAddDialog || !!editingNote}
        onOpenChange={(open) => {
          if (!open) { setShowAddDialog(false); setEditingNote(null); resetForm(); }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {dialogIsEditing ? `Edit ${dialogType}` : `Add ${dialogType}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note-content">{isNote ? "Note" : "Follow-Up Task"}</Label>
              <Textarea
                id="note-content"
                placeholder={isNote ? "Enter note..." : "Enter follow-up task description..."}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={4}
                data-testid="note-content-input"
              />
            </div>

            {!isNote && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="assigned-to">Assign To *</Label>
                  <Select
                    value={formData.assigned_to_id || ""}
                    onValueChange={(v) => setFormData({ ...formData, assigned_to_id: v })}
                  >
                    <SelectTrigger data-testid="assign-to-select">
                      <SelectValue placeholder="Select user..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...users].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.full_name || u.name || u.username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due-date">Due Date *</Label>
                  <Input
                    id="due-date" type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    data-testid="due-date-input"
                  />
                </div>
              </div>
            )}

            {!isNote && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                <p className="text-blue-800 dark:text-blue-200">
                  A reminder will be sent to the assigned user 24 hours before the due date (at 3:45 PM CT).
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setEditingNote(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!formData.content.trim() || (!isNote && (!formData.assigned_to_id || !formData.due_date))} data-testid="save-note-btn">
              {dialogIsEditing ? "Save Changes" : `Add ${dialogType}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Completion Note Dialog ─────────── */}
      <Dialog open={!!completionDialog} onOpenChange={(open) => { if (!open) { setCompletionDialog(null); setCompletionNoteText(""); } }}>
        <DialogContent data-testid="completion-note-dialog">
          <DialogHeader>
            <DialogTitle>
              {completionDialog?._editOnly ? "Edit Completion Note" : "Mark Follow-Up Complete"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!completionDialog?._editOnly && (
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium">Task:</p>
                <p className="text-muted-foreground mt-1">{completionDialog?.content}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="completion-note">
                Note back to {completionDialog?.created_by_name || "creator"} (optional)
              </Label>
              <Textarea
                id="completion-note"
                value={completionNoteText}
                onChange={(e) => setCompletionNoteText(e.target.value)}
                placeholder="Leave a note about what was done..."
                rows={3}
                data-testid="completion-note-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompletionDialog(null); setCompletionNoteText(""); }}>
              Cancel
            </Button>
            <Button
              onClick={completionDialog?._editOnly ? handleSaveCompletionNote : handleConfirmComplete}
              data-testid="confirm-complete-btn"
            >
              {completionDialog?._editOnly ? "Save Note" : "Complete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotesTab;
